import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh,
  Tune as TuneIcon,
  Place,
  Search,
  Chat,
  AutoAwesome,
  Payments,
  Email,
  Public as PublicIcon,
} from '@mui/icons-material';
import { useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { domainSettingsApi } from '../services/api';
import { PublicPageTemplate } from '../types/company';
import {
  DomainSettingsFormData,
  emptyDomainSettingsFormData,
  mapDomainSettingsToFormData,
} from '../types/domainSettings';
import { settingsCardSx } from '../settings/companySettingsShared';
import { DOMAIN_SETTINGS_QUERY_KEY, useDomainSettingsQuery } from '../settings/useDomainSettingsQuery';
import { SettingsPageHeader } from '../settings/SettingsPageHeader';

export const DomainSettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const companyId = user?.company?.id;

  const [isDirty, setIsDirty] = useState(false);
  const [settingsData, setSettingsData] = useState<DomainSettingsFormData>(
    emptyDomainSettingsFormData
  );

  const settingsQuery = useDomainSettingsQuery(companyId);

  useEffect(() => {
    if (!settingsQuery.data) {
      return;
    }

    setSettingsData(mapDomainSettingsToFormData(settingsQuery.data));
    setIsDirty(false);
  }, [settingsQuery.data]);

  const updateSettingsMutation = useMutation(
    (data: DomainSettingsFormData) => domainSettingsApi.updateSettings(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([DOMAIN_SETTINGS_QUERY_KEY, companyId]);
        toast.success(t('settings.messages.settingsUpdated'));
        setIsDirty(false);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || t('settings.messages.settingsUpdateFailed'));
      },
    }
  );

  const handleToggleChange = (field: keyof DomainSettingsFormData, value: boolean) => {
    setSettingsData((prev) => ({ ...prev, [field]: value }));
    setIsDirty(true);
  };

  const handleTextFieldChange = (field: keyof DomainSettingsFormData, value: string) => {
    setSettingsData((prev) => ({ ...prev, [field]: value }));
    setIsDirty(true);
  };

  const handleTemplateChange = (value: PublicPageTemplate) => {
    setSettingsData((prev) => ({ ...prev, publicPageTemplate: value }));
    setIsDirty(true);
  };

  const handleSaveSettings = () => {
    updateSettingsMutation.mutate(settingsData);
  };

  if (settingsQuery.isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 2 }}>
      <SettingsPageHeader
        icon={<TuneIcon color="primary" />}
        title={t('settings.domainSettingsTitle', { defaultValue: 'Domain Settings' })}
        subtitle={t('settings.domainSettingsSubtitle', {
          defaultValue:
            'Control which features are enabled for your company. Some features may still be in development.',
        })}
        actions={
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            sx={{ width: { xs: '100%', lg: 'auto' } }}
          >
            <Tooltip title={t('common.refresh')}>
              <span>
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={() => settingsQuery.refetch()}
                  sx={{ minWidth: 150 }}
                >
                  {t('common.refresh')}
                </Button>
              </span>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={
                updateSettingsMutation.isLoading ? <CircularProgress size={18} /> : <SaveIcon />
              }
              onClick={handleSaveSettings}
              disabled={!isDirty || updateSettingsMutation.isLoading}
              sx={{ minWidth: 180 }}
            >
              {t('common.saveChanges')}
            </Button>
          </Stack>
        }
      />

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper sx={settingsCardSx}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
              <Place color="primary" />
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="h6">
                  {t('settings.enableMultipleLocations', {
                    defaultValue: 'Enable Multiple Locations',
                  })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('settings.enableMultipleLocationsHint', {
                    defaultValue:
                      'Allow this company to manage multiple branch locations for vehicle assignment and rental pickup/return.',
                  })}
                </Typography>
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={settingsData.multipleLocations}
                    onChange={(e) => handleToggleChange('multipleLocations', e.target.checked)}
                  />
                }
                label=""
                sx={{ m: 0 }}
              />
            </Stack>

            <TextField
              fullWidth
              label={t('settings.googleMapsApiKey', { defaultValue: 'Google Maps API Key' })}
              helperText={t('settings.googleMapsApiKeyHint', {
                defaultValue:
                  'Used for address geocoding and location lookups. Leave blank to use the system default key.',
              })}
              value={settingsData.googleMapsApiKey}
              onChange={(e) => handleTextFieldChange('googleMapsApiKey', e.target.value)}
              sx={{ mt: 3 }}
            />

            <Divider sx={{ my: 3 }} />

            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
              <Email color="primary" />
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="h6">
                  {t('settings.enableEmailConnectivity', {
                    defaultValue: 'Enable Email Connectivity',
                  })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('settings.enableEmailConnectivityHint', {
                    defaultValue:
                      'Show the Email settings page so this company can connect an SMTP server and send emails. Enabled by default.',
                  })}
                </Typography>
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={settingsData.emailConnectivity}
                    onChange={(e) => handleToggleChange('emailConnectivity', e.target.checked)}
                  />
                }
                label=""
                sx={{ m: 0 }}
              />
            </Stack>

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
              {t('settings.upcomingFeatures', { defaultValue: 'Upcoming Features' })}
            </Typography>

            <Stack spacing={2.5}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Search color="disabled" />
                <Box sx={{ flexGrow: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body1">
                      {t('settings.enableSearchPage', { defaultValue: 'Enable Search Page' })}
                    </Typography>
                    <Chip
                      size="small"
                      label={t('settings.notYetEnforced', { defaultValue: 'Not yet enforced' })}
                    />
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {t('settings.enableSearchPageHint', {
                      defaultValue: 'Allow customers to search and browse available vehicles publicly.',
                    })}
                  </Typography>
                </Box>
                <Switch
                  checked={settingsData.searchPage}
                  onChange={(e) => handleToggleChange('searchPage', e.target.checked)}
                />
              </Stack>

              <Stack direction="row" spacing={1.5} alignItems="center">
                <Chat color="disabled" />
                <Box sx={{ flexGrow: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body1">
                      {t('settings.enableWhatsApp', { defaultValue: 'Enable WhatsApp' })}
                    </Typography>
                    <Chip
                      size="small"
                      label={t('settings.notYetEnforced', { defaultValue: 'Not yet enforced' })}
                    />
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {t('settings.enableWhatsAppHint', {
                      defaultValue: 'Allow this company to send and receive WhatsApp messages.',
                    })}
                  </Typography>
                </Box>
                <Switch
                  checked={settingsData.whatsApp}
                  onChange={(e) => handleToggleChange('whatsApp', e.target.checked)}
                />
              </Stack>

              <Stack spacing={2}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <AutoAwesome color="disabled" />
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="body1">
                      {t('settings.enableAI', { defaultValue: 'Enable AI' })}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('settings.enableAIHint', {
                        defaultValue: 'Allow this company to use AI-assisted features.',
                      })}
                    </Typography>
                  </Box>
                  <Switch
                    checked={settingsData.ai}
                    onChange={(e) => handleToggleChange('ai', e.target.checked)}
                  />
                </Stack>
                <Stack spacing={2} sx={{ pl: { sm: 5 } }}>
                  <TextField
                    fullWidth
                    disabled={!settingsData.ai}
                    label={t('settings.aiApiKey', { defaultValue: 'AI API Key' })}
                    helperText={t('settings.aiApiKeyHint', {
                      defaultValue:
                        'OpenAI API key used for AI-assisted document extraction. Leave blank to use the system default key.',
                    })}
                    value={settingsData.aiApiKey}
                    onChange={(e) => handleTextFieldChange('aiApiKey', e.target.value)}
                  />
                  <TextField
                    fullWidth
                    disabled={!settingsData.ai}
                    label={t('settings.aiModel', { defaultValue: 'AI Model' })}
                    helperText={t('settings.aiModelHint', {
                      defaultValue:
                        'OpenAI model name (e.g. gpt-4o-mini). Leave blank to use the system default model.',
                    })}
                    value={settingsData.aiModel}
                    onChange={(e) => handleTextFieldChange('aiModel', e.target.value)}
                  />
                </Stack>
              </Stack>

              <Stack direction="row" spacing={1.5} alignItems="center">
                <Payments color="disabled" />
                <Box sx={{ flexGrow: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body1">
                      {t('settings.enableDynamicPricing', {
                        defaultValue: 'Enable Dynamic Pricing',
                      })}
                    </Typography>
                    <Chip
                      size="small"
                      label={t('settings.notYetEnforced', { defaultValue: 'Not yet enforced' })}
                    />
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {t('settings.enableDynamicPricingHint', {
                      defaultValue: 'Allow this company to use demand-based dynamic pricing rules.',
                    })}
                  </Typography>
                </Box>
                <Switch
                  checked={settingsData.dynamicPricing}
                  onChange={(e) => handleToggleChange('dynamicPricing', e.target.checked)}
                />
              </Stack>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={settingsCardSx}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
              <PublicIcon color="primary" />
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="h6">
                  {t('fields.publicPageTemplate', { defaultValue: 'Page Layout' })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('settings.publicPageTemplateOwnerHint', {
                    defaultValue:
                      'Controls the structure of this company\'s public search/booking page. Tenant admins cannot change this - only you can.',
                  })}
                </Typography>
              </Box>
            </Stack>

            <TextField
              select
              fullWidth
              label={t('fields.publicPageTemplate', { defaultValue: 'Page Layout' })}
              value={settingsData.publicPageTemplate}
              onChange={(e) => handleTemplateChange(e.target.value as PublicPageTemplate)}
              helperText={t('settings.publicPageTemplateHint', {
                defaultValue: 'Choose how this company\'s public search page is laid out.',
              })}
              sx={{ maxWidth: 420 }}
            >
              <MenuItem value="CLASSIC">
                {t('settings.publicPageTemplateClassic', { defaultValue: 'Classic - full marketing page' })}
              </MenuItem>
              <MenuItem value="MINIMAL">
                {t('settings.publicPageTemplateMinimal', { defaultValue: 'Minimal - simple booking page' })}
              </MenuItem>
            </TextField>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              size="large"
              startIcon={
                updateSettingsMutation.isLoading ? <CircularProgress size={18} /> : <SaveIcon />
              }
              onClick={handleSaveSettings}
              disabled={!isDirty || updateSettingsMutation.isLoading}
            >
              {t('settings.saveSettings')}
            </Button>
          </Box>
        </Grid>
      </Grid>

      {settingsQuery.isError && (
        <Alert severity="error" sx={{ mt: 3 }}>
          {t('common.error', { defaultValue: 'Error' })}
        </Alert>
      )}
    </Container>
  );
};
